<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
  fixedStatus: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { attributeFilterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};
const CONVERSATION_STATUS_ATTRIBUTE = 'status';

const { t } = useI18n();
const store = useStore();

const fixedStatusFilter = () => ({
  ...DEFAULT_FILTER,
  values: [
    {
      id: props.fixedStatus,
      name: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${props.fixedStatus}.TEXT`),
    },
  ],
});

const getQueryOperator = (filter, index, total) => {
  if (index === total - 1) return undefined;
  if (index === 0) return 'and';
  return filter.queryOperator || 'and';
};

const normalizeFixedStatusFilters = filterList => {
  if (!props.fixedStatus) return filterList;

  const currentFilters = Array.isArray(filterList) ? filterList : [];
  const otherFilters = currentFilters
    .filter(
      filter =>
        (filter.attributeKey || filter.attribute_key) !==
        CONVERSATION_STATUS_ATTRIBUTE
    )
    .map(filter => ({
      ...filter,
      attributeKey: filter.attributeKey || filter.attribute_key,
      filterOperator: filter.filterOperator || filter.filter_operator,
      queryOperator: filter.queryOperator || filter.query_operator,
    }));
  const statusFilter = fixedStatusFilter();

  const allFilters = [statusFilter, ...otherFilters];
  return allFilters.map((filter, index) => ({
    ...filter,
    queryOperator: getQueryOperator(filter, index, allFilters.length),
  }));
};

const selectableAttributeFilterTypes = computed(() => {
  if (!props.fixedStatus) return attributeFilterTypes.value;
  return attributeFilterTypes.value.filter(
    type => type.disabled || type.attributeKey !== CONVERSATION_STATUS_ATTRIBUTE
  );
});

const filterTypesFor = filter => {
  const attribute = filter.attributeKey || filter.attribute_key;
  return attribute === CONVERSATION_STATUS_ATTRIBUTE
    ? attributeFilterTypes.value
    : selectableAttributeFilterTypes.value;
};

const isLockedFilter = filter =>
  Boolean(
    props.fixedStatus &&
      (filter.attributeKey || filter.attribute_key) ===
        CONVERSATION_STATUS_ATTRIBUTE
  );

if (props.fixedStatus) {
  filters.value = normalizeFixedStatusFilters(filters.value);
}

const resetFilter = () => {
  filters.value = [
    props.fixedStatus ? fixedStatusFilter() : { ...DEFAULT_FILTER },
  ];
};

const removeFilter = index => {
  if (
    props.fixedStatus &&
    (filters.value[index]?.attributeKey ||
      filters.value[index]?.attribute_key) === CONVERSATION_STATUS_ATTRIBUTE
  ) {
    return;
  }
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({
    ...DEFAULT_FILTER,
    ...(props.fixedStatus ? { attributeKey: 'priority' } : {}),
  });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  filters.value = normalizeFixedStatusFilters(filters.value);
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 w-[min(34rem,calc(100vw-2rem))] lg:w-[750px] overflow-visible border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isFolderView">
      <div class="border-b border-n-weak pb-6">
        <Input
          v-model="folderNameLocal"
          :label="t('FILTER.FOLDER_LABEL')"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>
    <ul class="grid gap-4 list-none min-w-0">
      <template v-for="(filter, index) in filters" :key="filter.id">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypesFor(filter)"
          :locked="isLockedFilter(filter)"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypesFor(filter)"
          :locked="isLockedFilter(filter)"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
